import React, { FC, useEffect, useState } from 'react';
import EmployeeThumb from '../../components/EmployeeThumb';
import Cta from '../../components/buttons/Cta';
import LoadingSpinner from '../../components/buttons/LoadingSpinner';
import Table from '../../components/tables/Table';
import TableItem from '../../components/tables/TableItem';
import TableRow from '../../components/tables/TableRow';
import { Timer } from '../../components/tickets/Timer';
import Typography from '../../components/typography/Typography';
import EmployeeSelect from '../../components/users/EmployeeSelect';
import { TicketTimeEnty } from '../../interfaces/Ticket';
import { TicketService } from '../../services/tickets/Tickets';
import { UsersService } from '../../services/users/Users';
import ListComponent from '../../utils/ListComponent';
import { DateUtil } from '../../utils/dates/DateUtil';
import { UtcToLocal, UtcToLocalTime } from '../../utils/dates/UtcToLocal';

interface TimeEntry {
  id: number; // Assuming each entry has a unique ID
  med_foto: string;
  med_naam: string;
  created_at: Date;
  start_timestamp: Date;
  end_timestamp: Date;
  status: string;
}

interface TicketTimeEntriesProps {
  entries: TicketTimeEnty[];
  ticket_id: number;
  setTimeEntries: any;
}

const tableHeaders = [
  '',
  'User / Date',
  'Start Time',
  'End Time',
  'Duration / Actions',
];

const TicketTimeEntries: FC<TicketTimeEntriesProps> = ({
  entries,
  ticket_id,
  setTimeEntries,
}) => {
  const ticketService = new TicketService();
  const userService = new UsersService();

  const [employees, setEmployees] = useState();
  const [employee, setEmployee] = useState();
  const [loading, setLoading] = useState(false);

  const handleAddEntry = async () => {
    setLoading(true);
    const update = {
      emp_id: employee,
      start_timestamp: new Date(),
    };
    const t = await ticketService.addTicketTimeEntry(ticket_id, update);
    const entry = await ticketService.getTicketTimeEntries(ticket_id);
    setTimeEntries(entry.entries);
    setLoading(false);
  };

  const handleTimerStart = async (
    entryId: number,
    elapsedTime?: number,
    type?: string
  ) => {
    const update = {
      end_timestamp: type === 'resume' ? undefined : new Date(),
      emp_id: 1,
      total_elapsed_time: elapsedTime,
      status: type === 'resume' ? 'resumed' : 'pause',
    };

    const entry = await ticketService.updateTicketTimeEntry(
      ticket_id,
      entryId,
      {
        ...update,
      }
    );
    const updatedValues = entries.map((item) => {
      if (item.id === entryId) {
        return {
          ...item,
          ...update,
        };
      }
      return item;
    });

    setTimeEntries(updatedValues);
  };

  const handleTimerStop = async (entryId: number, duration: number) => {
    const update = {
      end_timestamp: new Date(),
      emp_id: 1,
      total_elapsed_time: duration,
      total_time_rounded: DateUtil.roundTimeUpTo(duration, 15),
      status: 'closed',
    };

    const entry = await ticketService.updateTicketTimeEntry(
      ticket_id,
      entryId,
      {
        ...update,
      }
    );
  };

  const fetchEmployees = async () => {
    const employeesResult = await userService.getAllEmployees();
    setEmployees(employeesResult.employees);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  return (
    <Table header={tableHeaders}>
      {/* Create new entry */}
      <TableRow key="1">
        <TableItem> </TableItem>
        <TableItem colspan={3}>
          <div className="flex items-center">
            {employees ? (
              <EmployeeSelect
                employees={employees}
                option={ticket_id}
                onChange={setEmployee}
                placeholder={'Wie ben je?'}
              />
            ) : (
              <LoadingSpinner />
            )}
          </div>
        </TableItem>
        <TableItem>
          <Cta
            style="small"
            color="default"
            onClick={handleAddEntry}
            text="Start"
            disabled={!employee}
          />
        </TableItem>
      </TableRow>
      {entries && entries.length > 0 ? (
        <ListComponent
          data={entries}
          renderItem={(item) => (
            <TableRow key={item.id}>
              <TableItem>
                <EmployeeThumb image={item.med_foto} name={item.med_naam} />
              </TableItem>
              <TableItem>
                <div className="grid grid-cols-">
                  <Typography tag="span" type="label">
                    {item.med_naam}
                  </Typography>
                  <UtcToLocal utcDate={item.created_at} />
                </div>
              </TableItem>
              <TableItem>
                <UtcToLocalTime utcDate={item.start_timestamp} />
                {item.status}
              </TableItem>
              <TableItem>
                <UtcToLocalTime utcDate={item.end_timestamp} />
              </TableItem>
              <TableItem>
                <Timer
                  time={item.start_timestamp}
                  entryId={item.id}
                  onStart={handleTimerStart}
                  onStop={handleTimerStop}
                  status={item.status}
                  endTime={item.end_timestamp}
                  startTime={item.start_timestamp}
                  elapsed={item.total_elapsed_time}
                />
              </TableItem>
            </TableRow>
          )}
        />
      ) : (
        <TableRow>
          <TableItem colSpan={tableHeaders.length}>
            <Typography tag="span" type="body">
              No time entries available.
            </Typography>
          </TableItem>
        </TableRow>
      )}
    </Table>
  );
};

export default TicketTimeEntries;
